import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';

const TooltipContainer = styled.div<{ visible?: boolean }>`
  border: 1px solid var(--text-primary);
  background: var(--page-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  white-space: nowrap;
  padding: 0.75em 1.25em;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.15s ease 0s;
  z-index: 9999;
`;

const Icon = styled.img`
  display: inline-block;
  height: 1rem;
  width: auto;
`;

interface Props {
  visible: boolean;
  version: string | { [key: string]: any };
  attributes?: { [key: string]: any };
  referenceEl: Element | null;
}

const ShowtimeTooltip: React.FC<Props> = ({ visible, version, attributes, referenceEl }) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: referenceEl !== null ? referenceEl : undefined,
    },
  });

  if (typeof version === 'string') return null;

  return createPortal(
    <TooltipContainer innerRef={refs.setFloating} style={floatingStyles} visible={visible}>
      {[...version.type]
        .filter(type => !type.startsWith('lang_'))
        .sort((a, b) => {
          const hasIconA = !!(attributes && attributes[a] && attributes[a].images && attributes[a].images.icon && attributes[a].images.icon.png);
          const hasIconB = !!(attributes && attributes[b] && attributes[b].images && attributes[b].images.icon && attributes[b].images.icon.png);
          return hasIconA === hasIconB ? 0 : hasIconA ? -1 : 1;
        })
        .map(type => {
          if (!attributes || !attributes[type]) return null;
          const attribute = attributes[type];
          const hasIcon = !!(attribute.images && attribute.images.icon && attribute.images.icon.png);
          
          return (
            <span key={type} style={{ display: 'block' }}>
              {hasIcon && <>
                <Icon
                  src={attribute.images.traditional.default.png}
                  alt={attribute.display_name}
                  style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
                  className='d-traditional-only'
                />
                <Icon
                  src={attribute.images.platinum.default.png}
                  alt={attribute.display_name}
                  style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
                  className='d-platinum-only'
                />
                <Icon
                  src={attribute.images.market.default.png}
                  alt={attribute.display_name}
                  style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}
                  className='d-market-only'
                />
              </>}
              {attribute.display_name}
            </span>
          );
        })}
    </TooltipContainer>,
    document.body
  );
};

export default ShowtimeTooltip;
